import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import close from '../../../../assets/icons/close.svg';
import Notifier from '../../../../Utils/notify';
import { getData, Postdata } from '../../../../API/GlobalApi';
import '../../../../assets/css/Modal.css';
import { StudentLedgerDetails } from '../../../../Component/Modals/StudentLegerDetails';

const fmtMoney = (v) =>
  v === null || v === undefined
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── PIN Step ──────────────────────────────────────────────────────────────────
function PinStep({ count, onBack, onConfirm, submitting }) {
  const [pin, setPin] = useState('');
  const pinRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => pinRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  const ready = pin.length === 4 && !submitting;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{
        background: '#f0fdf4',
        border: '1px dashed #86efac',
        borderRadius: 8,
        padding: '14px 16px',
        fontSize: 13,
        color: '#166534',
        lineHeight: 1.6,
      }}>
        You are about to submit the <strong>Finance Reconciliation Report</strong> for{' '}
        <strong>{count} student{count !== 1 ? 's' : ''}</strong>.
        <br />
        This action cannot be undone without a separate revoke. Enter your security PIN to confirm.
      </div>

      <div>
        <label className="form-labell">Security PIN</label>
        <input
          ref={pinRef}
          type="password"
          inputMode="numeric"
          maxLength={4}
          className="form-control fm-modal"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          placeholder="Enter your 4-digit PIN"
          autoComplete="off"
          style={{ maxWidth: 240 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onConfirm(pin);
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
        <button
          onClick={onBack}
          disabled={submitting}
          style={{
            padding: '10px 18px',
            background: '#fff',
            color: '#222F3E',
            border: '1px solid #d3d3e8',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          ← Back
        </button>
        <button
          onClick={() => onConfirm(pin)}
          disabled={!ready}
          style={{
            padding: '10px 22px',
            background: ready ? '#7F56DA' : '#d3d3d3',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 14,
            cursor: ready ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {submitting && (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          )}
          {submitting ? 'Submitting…' : 'Submit Reports'}
        </button>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function BulkApprovalModal({
  isOpen,
  onClose,
  activityId,
  onSuccess,
  onRefreshList,
}) {
  const [students, setStudents] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [step, setStep] = useState('list');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null | 'pending' | 'submitted'
  const [ledgerDetails, setLedgerDetails] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  const openLedger = async (e, student) => {
    e.stopPropagation();
    try {
      const res = await getData(`students/get-student-ledger-details/${student.student_id}/`);
      const data = res?.data ?? res;
      if (data) setLedgerDetails({
        id: data.student_id,
        name: data.student_name,
        profile_type: data.student_profile,
        course_name: data.course_name,
        contact_number: data.primary_contact_number,
        permanent_registration_number: data.permanent_registration_number,
        last_credit_payment_date: data.last_credit_payment_date,
        last_credit_amount: data.last_credit_amount,
        last_credit_fee_head: data.last_credit_fee_head,
        ledger_data: data.ledger_data || [],
        academic_groups: data.academic_groups,
        payment_schedule: data.payment_schedule || [],
        payment_fee_plan: data.payment_fee_plan || [],
        financial_summary: data.financial_summary,
      });
      setLedgerOpen(true);
    } catch {
      Notifier.error('Could not load student ledger');
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setStep('list');
    setSubmitting(false);
    setStatusFilter(null);
    fetchStudents();
  }, [isOpen]);

  const fetchStudents = async () => {
    setFetchLoading(true);
    try {
      const res = await getData(
        `academics/activities/reconciliation-report/bulk/accounts-list/?activity_id=${activityId}`
      );
      setStudents(res?.results || []);
    } catch (e) {
      Notifier.error('Failed to load student list');
      setStudents([]);
    } finally {
      setFetchLoading(false);
    }
  };

  if (!isOpen) return null;

  const submittedCount = students.filter((s) => s.accounts_report?.submitted).length;
  const pendingCount = students.length - submittedCount;

  const visibleStudents = statusFilter === 'pending'
    ? students.filter((s) => !s.accounts_report?.submitted)
    : statusFilter === 'submitted'
      ? students.filter((s) => s.accounts_report?.submitted)
      : students;

  // Students without an existing active report are selectable
  const selectableStudents = students.filter((s) => !s.accounts_report?.submitted);
  const selectableIds = selectableStudents.map((s) => s.student_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleFilter = (val) => setStatusFilter((prev) => (prev === val ? null : val));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };

  const toggleOne = (s) => {
    if (s.accounts_report?.submitted) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(s.student_id) ? next.delete(s.student_id) : next.add(s.student_id);
      return next;
    });
  };

  const handlePinConfirm = async (pin) => {
    if (pin.length !== 4) { Notifier.error('PIN must be 4 digits'); return; }
    setSubmitting(true);
    try {
      const res = await Postdata(
        'academics/activities/reconciliation-report/bulk/accounts-submit/',
        {
          activity_id: activityId,
          student_ids: [...selected].join(','),
          pin,
        }
      );

      if (res?.error) {
        Notifier.error(res.error);
        return;
      }

      const { created = 0, skipped = 0, errored = 0 } = res?.summary || {};
      const parts = [];
      if (created > 0) parts.push(`${created} submitted`);
      if (skipped > 0) parts.push(`${skipped} skipped`);
      if (errored > 0) parts.push(`${errored} errored`);
      Notifier.success(`✅ Finance reports: ${parts.join(', ')}.`);
      onSuccess?.();
      onRefreshList?.();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Could not submit reports. Please retry.';
      Notifier.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {createPortal(
    <div className="modal-overlay" style={{ zIndex: 9999999 }}>
      <div
        className="modal-content"
        style={{
          position: 'relative',
          width: 'min(1080px, 96vw)',
          maxHeight: '90vh',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div
          className="modal-header-content"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 28px',
            borderBottom: '1px solid #E9E9E9',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ margin: 0, color: '#7F56DA', fontSize: 18, fontWeight: 700 }}>
              Bulk Submit Finance Report
            </h2>
            {step === 'list' && someSelected && (
              <span style={{
                background: '#7F56DA', color: '#fff',
                borderRadius: 999, padding: '2px 12px',
                fontSize: 12, fontWeight: 600,
              }}>
                {selected.size} selected
              </span>
            )}
          </div>
          <button className="close-btn" onClick={onClose} disabled={submitting}>
            <img src={close} alt="close" />
          </button>
        </div>

        {step === 'list' ? (
          <>
            {/* Summary chips — click to filter */}
            {!fetchLoading && students.length > 0 && (
              <div style={{ display: 'flex', gap: 10, margin: '14px 28px 8px', alignItems: 'center' }}>
                <span
                  onClick={() => toggleFilter('pending')}
                  style={{
                    background: statusFilter === 'pending' ? '#fbbf24' : '#fef9c3',
                    color: '#854d0e',
                    border: `1px solid ${statusFilter === 'pending' ? '#f59e0b' : '#fde68a'}`,
                    borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', userSelect: 'none',
                    boxShadow: statusFilter === 'pending' ? '0 0 0 2px #fde68a' : 'none',
                  }}
                  title="Click to filter pending"
                >
                  Pending: {pendingCount}
                </span>
                <span
                  onClick={() => toggleFilter('submitted')}
                  style={{
                    background: statusFilter === 'submitted' ? '#86efac' : '#f0fdf4',
                    color: '#16a34a',
                    border: `1px solid ${statusFilter === 'submitted' ? '#4ade80' : '#bbf7d0'}`,
                    borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', userSelect: 'none',
                    boxShadow: statusFilter === 'submitted' ? '0 0 0 2px #bbf7d0' : 'none',
                  }}
                  title="Click to filter submitted"
                >
                  Submitted: {submittedCount}
                </span>
                {statusFilter && (
                  <span
                    onClick={() => setStatusFilter(null)}
                    style={{
                      fontSize: 11, color: '#7F56DA', cursor: 'pointer',
                      textDecoration: 'underline', userSelect: 'none',
                    }}
                  >
                    Clear filter
                  </span>
                )}
              </div>
            )}

            {/* Table / Loader */}
            <div className="bulk-approval-scroll" style={{
              flex: 1,
              overflowY: 'auto',
              margin: '0 28px',
              border: '1px solid #E9E9E9',
              borderRadius: 8,
              maxHeight: 'calc(90vh - 160px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent',
            }}>
              {fetchLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0' }}>
                  <span className="spinner-border" style={{ color: '#7F56DA' }} role="status" />
                </div>
              ) : visibleStudents.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6B778C', padding: '60px 0', fontSize: 14 }}>
                  {students.length === 0 ? 'No students found for this activity.' : 'No students match the selected filter.'}
                </div>
              ) : (
                <table className="student-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40, padding: '10px 12px', textAlign: 'center', background: '#F9F9F9', borderBottom: '1px solid #E9E9E9', position: 'sticky', top: 0, zIndex: 1 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          title="Select all pending"
                          style={{ cursor: 'pointer', width: 14, height: 14 }}
                        />
                      </th>
                      <th style={{ ...TH, position: 'sticky', top: 0, zIndex: 1 }}>Name (Reg No)</th>
                      <th style={{ ...TH, textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }}>Debit</th>
                      <th style={{ ...TH, textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }}>Credit</th>
                      <th style={{ ...TH, textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }}>Imposed</th>
                      <th style={{ ...TH, textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }}>Balance</th>
                      <th style={{ ...TH, textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStudents.map((s, idx) => {
                      const isSubmitted = s.accounts_report?.submitted === true;
                      const isSelected = selected.has(s.student_id);
                      const selectable = !isSubmitted;
                      const isEven = idx % 2 === 0;

                      let rowBg = isEven ? '#FFFFFF' : '#F9F9F9';
                      if (isSubmitted) rowBg = isEven ? '#f9fffe' : '#f5fffe';
                      else if (isSelected) rowBg = '#f0fdf4';

                      return (
                        <tr
                          key={s.student_id}
                          onClick={() => toggleOne(s)}
                          className="bulk-approval-row"
                          style={{
                            background: rowBg,
                            cursor: selectable ? 'pointer' : 'default',
                            borderTop: '1px solid #F3F4F6',
                          }}
                        >
                          <td style={{ width: 40, padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!selectable}
                              onChange={() => toggleOne(s)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ cursor: selectable ? 'pointer' : 'not-allowed', width: 14, height: 14 }}
                            />
                          </td>
                          <td style={{ ...TD, minWidth: 140 }}>
                            <div style={{ color: '#222F3E', fontWeight: 500, fontSize: 12, lineHeight: 1.2 }}>
                              {s.full_name}
                            </div>
                            <div
                              onClick={(e) => openLedger(e, s)}
                              style={{ fontSize: 10, color: '#7F56DA', marginTop: 1, textDecoration: 'underline', cursor: 'pointer' }}
                              title="View Student Ledger"
                            >
                              {s.permanent_registration_number}
                            </div>
                          </td>
                          <td style={{ ...TD, textAlign: 'right', color: '#dc2626', fontWeight: 500, fontSize: 12 }}>
                            {fmtMoney(s.total_debit)}
                          </td>
                          <td style={{ ...TD, textAlign: 'right', color: '#16a34a', fontWeight: 500, fontSize: 12 }}>
                            {fmtMoney(s.total_credit)}
                          </td>
                          <td style={{ ...TD, textAlign: 'right', color: '#b45309', fontSize: 12 }}>
                            {fmtMoney(s.total_imposed)}
                          </td>
                          <td style={{
                            ...TD,
                            textAlign: 'right',
                            fontWeight: 600,
                            fontSize: 12,
                            color: Number(s.pending_amount) > 0 ? '#dc2626' : '#16a34a',
                          }}>
                            {fmtMoney(s.pending_amount)}
                          </td>
                          <td style={{ ...TD, textAlign: 'center' }}>
                            {isSubmitted ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                background: '#f0fdf4', color: '#16a34a',
                                border: '1px solid #bbf7d0',
                                borderRadius: 999, padding: '1px 8px',
                                fontSize: 11, fontWeight: 500,
                              }}>
                                Submitted ✓
                              </span>
                            ) : (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                background: '#fef9c3', color: '#854d0e',
                                border: '1px solid #fde68a',
                                borderRadius: 999, padding: '1px 8px',
                                fontSize: 11, fontWeight: 500,
                              }}>
                                Pending
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '0 28px',
              padding: '14px 0',
              borderTop: '1px solid #E9E9E9',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 13, color: '#6B778C' }}>
                {statusFilter
                  ? <><strong style={{ color: '#222F3E' }}>{visibleStudents.length}</strong> shown / </>
                  : null}
                <strong style={{ color: '#222F3E' }}>{students.length}</strong> total students
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={onClose}
                  style={{
                    padding: '10px 18px',
                    background: '#fff',
                    color: '#222F3E',
                    border: '1px solid #d3d3e8',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={!someSelected}
                  onClick={() => setStep('pin')}
                  style={{
                    padding: '10px 22px',
                    background: someSelected ? '#7F56DA' : '#d3d3d3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: someSelected ? 'pointer' : 'not-allowed',
                  }}
                >
                  Submit {someSelected ? `(${selected.size})` : ''} Selected →
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ padding: '24px 28px' }}>
            <PinStep
              count={selected.size}
              onBack={() => setStep('list')}
              onConfirm={handlePinConfirm}
              submitting={submitting}
            />
          </div>
        )}
      </div>
    </div>,
      document.body
    )}
      <StudentLedgerDetails
        isOpen={ledgerOpen}
        onClose={() => setLedgerOpen(false)}
        selectedFeeDetails={ledgerDetails}
      />
    </>
  );
}

const TH = {
  padding: '10px 14px',
  background: '#F9F9F9',
  color: '#6B778C',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'Monosans',
  borderBottom: '1px solid #E9E9E9',
  whiteSpace: 'nowrap',
};

const TD = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};
